import {
  AllowedCommandsResponse,
  ClientFilesResponse,
  Command,
  CompanyReference,
  CTEyeClientModel,
  CTEyeInspectionSummaryModel,
  CTEyeClientSummaryModel,
  CTEyeClientResponse,
  CTEyeClientUpdateRequest,
  CTEyeSubscription,
  DataFileDto,
  HeartbeatItemResponse,
  HeartbeatModel,
  HeartbeatRequest,
  ImportInspectionRequest,
  ImportIntoInspectionRequest,
  ImportableInspectionModel,
  ImportInspectionResponse,
  LiveInspectionResponse,
  LiveViewSessionResponse,
  ODataResponse,
  PurgeHeartbeatsResponse,
  QueuedCommandCreateRequest,
  RegisterClientRequest,
  StartLiveViewRequest,
  StartLiveViewResponse,
  SubscriptionUsageResponse,
} from "../entities";
import BaseRepository from "./BaseRepository";

export class CTEyeRepo extends BaseRepository {
  private baseUrl = "CTEye";

  // ── Client Endpoints ────────────────────────────────────────

  /**
   * GET /CTEye/clients
   * Get all CTEye clients
   */
  async getAllClients(): Promise<CTEyeClientResponse[] | null> {
    const url = `${this.baseUrl}/clients`;
    const response = await this.client.get<CTEyeClientResponse[]>(url);
    return this.handleResponse<CTEyeClientResponse[]>(response);
  }

  /**
   * GET /CTEye/companies/{companyId}/clients
   * Get CTEye clients for a specific company
   */
  async getClientsByCompanyId(companyId: number): Promise<CTEyeClientResponse[] | null> {
    const url = `${this.baseUrl}/companies/${companyId}/clients`;
    const response = await this.client.get<CTEyeClientResponse[]>(url);
    return this.handleResponse<CTEyeClientResponse[]>(response);
  }

  /**
   * GET /CTEye/clients/unregistered
   * Get all unregistered CTEye clients
   */
  async getUnregisteredClients(): Promise<CTEyeClientResponse[] | null> {
    const url = `${this.baseUrl}/unregistered-clients`;
    const response = await this.client.get<CTEyeClientResponse[]>(url);
    return this.handleResponse<CTEyeClientResponse[]>(response);
  }

  /**
   * POST /CTEye/clients/{clientId}/register
   * Register a CTEye client
   */
  async registerClient(clientId: string, request: RegisterClientRequest): Promise<boolean> {
    const url = `${this.baseUrl}/clients/${clientId}/register`;
    const response = await this.client.post(url, request);
    return response.success;
  }

  /**
   * GET /CTEye/clients/{clientId}
   * Get a single CTEye client.
   */
  async getClientById(clientId: string): Promise<CTEyeClientResponse | null> {
    const url = `${this.baseUrl}/clients/${clientId}`;
    const response = await this.client.get<CTEyeClientResponse>(url);
    return this.handleResponse<CTEyeClientResponse>(response);
  }

  /**
   * PUT /CTEye/clients/{clientId}
   * Update a CTEye client
   */
  async updateClient(clientId: string, request: CTEyeClientUpdateRequest): Promise<CTEyeClientResponse | null> {
    const url = `${this.baseUrl}/clients/${clientId}`;
    const body = {
      description: request.description,
      companyId: request.companyId ?? 0,
      logAllHeartbeats: request.logAllHeartbeats,
      monthlyInspectionLimit: request.monthlyInspectionLimit,
      monthlyReportLimit: request.monthlyReportLimit,
      gaugeSettings: request.gaugeSettings,
    };
    const response = await this.client.put<CTEyeClientResponse>(url, body);
    return this.handleResponse<CTEyeClientResponse>(response);
  }

  /**
   * POST /CTEye/clients/{clientId}/disable
   * Disable a CTEye client
   */
  async disableClient(clientId: string): Promise<boolean> {
    const url = `${this.baseUrl}/clients/${clientId}/disable`;
    const response = await this.client.post(url, {});
    return response.success;
  }

  /**
   * POST /CTEye/clients/{clientId}/enable
   * Enable a CTEye client
   */
  async enableClient(clientId: string): Promise<boolean> {
    const url = `${this.baseUrl}/clients/${clientId}/enable`;
    const response = await this.client.post(url, {});
    return response.success;
  }

  /**
   * GET /CTEye/clients/{clientId}/commands
   * Get allowed commands for a specific client
   */
  async getAllowedCommands(clientId: string): Promise<AllowedCommandsResponse | null> {
    const url = `${this.baseUrl}/clients/${clientId}/commands`;
    const response = await this.client.get<AllowedCommandsResponse>(url);
    return this.handleResponse<AllowedCommandsResponse>(response);
  }

  // ── Heartbeat Endpoints ─────────────────────────────────────

  /**
   * POST /CTEye/heartbeat
   * Process a heartbeat from a CTEye client
   */
  async heartbeat(request: HeartbeatRequest): Promise<Command[] | null> {
    const url = `${this.baseUrl}/heartbeat`;
    const response = await this.client.post<Command[]>(url, request);
    return this.handleResponse<Command[]>(response);
  }

  /**
   * GET /CTEye/clients/{clientId}/heartbeats
   * Get heartbeats for a specific client
   */
  async getHeartbeats(clientId: string, afterDate?: string): Promise<HeartbeatItemResponse[] | null> {
    let url = `${this.baseUrl}/clients/${clientId}/heartbeats`;
    if (afterDate) {
      url += `?afterDate=${encodeURIComponent(afterDate)}`;
    }
    const response = await this.client.get<HeartbeatItemResponse[]>(url);
    return this.handleResponse<HeartbeatItemResponse[]>(response);
  }

  /**
   * GET /CTEye/clients/{clientId}/heartbeats/latest
   * Get most recent heartbeat for a specific client
   */
  async getLatestHeartbeat(clientId: string): Promise<HeartbeatItemResponse | null> {
    const url = `${this.baseUrl}/clients/${clientId}/heartbeats/latest`;
    const response = await this.client.get<HeartbeatItemResponse>(url);
    return this.handleResponse<HeartbeatItemResponse>(response);
  }

  /**
   * DELETE /CTEye/clients/{clientId}/heartbeats
   * Purge heartbeats for a specific client before a given date
   */
  async purgeHeartbeats(clientId: string, beforeDate: string): Promise<PurgeHeartbeatsResponse | null> {
    const url = `${this.baseUrl}/clients/${clientId}/heartbeats?beforeDate=${encodeURIComponent(beforeDate)}`;
    const response = await this.client.delete<PurgeHeartbeatsResponse>(url);
    return this.handleResponse<PurgeHeartbeatsResponse>(response);
  }

  // ── File & Upload Endpoints ─────────────────────────────────

  /**
   * GET /CTEye/clients/{clientId}/files
   * Get files for a specific client
   */
  async getClientFiles(clientId: string): Promise<ClientFilesResponse | null> {
    const url = `${this.baseUrl}/clients/${clientId}/files`;
    const response = await this.client.get<ClientFilesResponse>(url);
    return this.handleResponse<ClientFilesResponse>(response);
  }


  /**
   * POST /CTEye/clients/{clientId}/upload-sessions/{sessionId}/dismiss
   * Dismiss an upload session for a specific client
   */
  async dismissUploadSession(clientId: string, sessionId: string): Promise<boolean> {
    const url = `${this.baseUrl}/clients/${clientId}/upload-sessions/${sessionId}/dismiss`;
    const response = await this.client.post(url, {});
    return response.success;
  }

  // ── Import Endpoints ────────────────────────────────────────

  /**
   * POST /CTEye/clients/{clientId}/import-inspection
   * Import an inspection from a CTEye data file
   */
  async importInspection(clientId: string, request: ImportInspectionRequest): Promise<ImportInspectionResponse | null> {
    const url = `${this.baseUrl}/clients/${clientId}/import-inspection`;
    const response = await this.client.post<ImportInspectionResponse>(url, request);
    return this.handleResponse<ImportInspectionResponse>(response);
  }

  /**
   * POST /CTEye/clients/{clientId}/import-inspection/{inspectionId}
   * Import a CTEye data file into an existing inspection, replacing its data
   */
  async importIntoInspection(clientId: string, inspectionId: number, request: ImportIntoInspectionRequest): Promise<ImportInspectionResponse | null> {
    const url = `${this.baseUrl}/clients/${clientId}/import-inspection/${inspectionId}`;
    const response = await this.client.post<ImportInspectionResponse>(url, request);
    return this.handleResponse<ImportInspectionResponse>(response);
  }

  /**
   * GET /CTEye/clients/{clientId}/importable-inspections
   * List inspections that data can be imported into for this client's company
   */
  async getImportableInspections(clientId: string): Promise<ImportableInspectionModel[] | null> {
    const url = `${this.baseUrl}/clients/${clientId}/importable-inspections`;
    const response = await this.client.get<ImportableInspectionModel[]>(url);
    return this.handleResponse<ImportableInspectionModel[]>(response);
  }

  // ── Live View Endpoints ─────────────────────────────────────

  /**
   * POST /CTEye/live-view
   * Start a live view session for a specific client
   */
  async startLiveView(request: StartLiveViewRequest): Promise<StartLiveViewResponse | null> {
    const url = `${this.baseUrl}/live-view`;
    const response = await this.client.post<StartLiveViewResponse>(url, request);
    return this.handleResponse<StartLiveViewResponse>(response);
  }

  /**
   * GET /CTEye/sessions/{sessionId}/live-view-data
   * Poll live view data and update LastRequestedDate
   */
  async getLiveViewData(sessionId: string): Promise<LiveViewSessionResponse | null> {
    const url = `${this.baseUrl}/sessions/${sessionId}/live-view-data`;
    const response = await this.client.get<LiveViewSessionResponse>(url);
    return this.handleResponse<LiveViewSessionResponse>(response);
  }

  // ── Command Endpoints ───────────────────────────────────────

  /**
   * POST /CTEye/command
   * Queue a command to be sent to a CTEye client
   */
  async queueCommand(request: QueuedCommandCreateRequest): Promise<boolean> {
    const url = `${this.baseUrl}/command`;
    const response = await this.client.post(url, request);
    return response.success;
  }

  // ── Subscription Endpoints ──────────────────────────────────

  /**
   * GET /CTEye/clients/{clientId}/subscription
   * Get the active subscription for a specific client
   */
  async getActiveSubscription(clientId: string): Promise<CTEyeSubscription | null> {
    const url = `${this.baseUrl}/clients/${clientId}/subscription`;
    const response = await this.client.get<CTEyeSubscription>(url);
    return this.handleResponse<CTEyeSubscription>(response);
  }

  /**
   * PUT /CTEye/clients/{clientId}/subscription
   * Update the subscription for a specific client
   */
  async updateSubscription(clientId: string, request: CTEyeSubscription): Promise<CTEyeSubscription | null> {
    const url = `${this.baseUrl}/clients/${clientId}/subscription`;
    const response = await this.client.put<CTEyeSubscription>(url, request);
    return this.handleResponse<CTEyeSubscription>(response);
  }


  /**
   * GET /CTEye/subscription/usage
   * Get subscription usage for the current user's company
   */
  async getSubscriptionUsage(): Promise<SubscriptionUsageResponse[]> {
    const url = `${this.baseUrl}/subscription/usage`;
    const response = await this.client.get<SubscriptionUsageResponse[]>(url);
    return this.handleResponse<SubscriptionUsageResponse[]>(response) ?? [];
  }

  // ── OData Endpoints ─────────────────────────────────────────

  /**
   * GET /OData/CTEyeClients
   * Get all CTEye clients with OData query support
   */
  async getClientsOData(oDataQuery: string = ""): Promise<ODataResponse<CTEyeClientModel>> {
    const url = `/OData/CTEyeClients?$count=true&${oDataQuery}`;
    const response = await this.client.get<ODataResponse<CTEyeClientModel>>(url);
    return this.handleResponse<ODataResponse<CTEyeClientModel>>(response)!;
  }

  /**
   * GET /OData/CTEyeClients/Default.Summaries
   * Get client summaries with subscription usage data
   */
  async getClientsSummariesOData(oDataQuery: string = ""): Promise<ODataResponse<CTEyeClientSummaryModel>> {
    const url = `/OData/CTEyeClients/Default.Summaries?$count=true&${oDataQuery}`;
    const response = await this.client.get<ODataResponse<CTEyeClientSummaryModel>>(url);
    return this.handleResponse<ODataResponse<CTEyeClientSummaryModel>>(response)!;
  }

  /**
   * GET /OData/CTEyeClients(guid)
   * Get a single CTEye client by ID
   */
  async getClientByIdOData(clientId: string): Promise<CTEyeClientModel | null> {
    const url = `/OData/CTEyeClients(${clientId})?$expand=Company`;
    const response = await this.client.get<CTEyeClientModel>(url);
    return this.handleResponse<CTEyeClientModel>(response);
  }

  /**
   * GET /OData/CTEyeClients(guid)/Heartbeats
   * Get heartbeats for a specific client with OData query support
   */
  async getClientHeartbeatsOData(clientId: string, oDataQuery: string = ""): Promise<ODataResponse<HeartbeatModel>> {
    const url = `/OData/CTEyeClients(${clientId})/Heartbeats?$count=true&${oDataQuery}`;
    const response = await this.client.get<ODataResponse<HeartbeatModel>>(url);
    return this.handleResponse<ODataResponse<HeartbeatModel>>(response)!;
  }

  /**
   * GET /OData/CTEyeClients(guid)/Company
   * Get the company associated with a specific client
   */
  async getClientCompanyOData(clientId: string): Promise<CompanyReference | null> {
    const url = `/OData/CTEyeClients(${clientId})/Company`;
    const response = await this.client.get<CompanyReference>(url);
    return this.handleResponse<CompanyReference>(response);
  }

  /**
   * GET /OData/CTEyeClients(guid)/DataFiles
   * Get data files for a specific client
   */
  async getClientDataFilesOData(clientId: string, oDataQuery: string = ""): Promise<ODataResponse<DataFileDto>> {
    const url = `/OData/CTEyeClients(${clientId})/DataFiles?$count=true&${oDataQuery}`;
    const response = await this.client.get<ODataResponse<DataFileDto>>(url);
    return this.handleResponse<ODataResponse<DataFileDto>>(response)!;
  }

  /**
   * GET /OData/CTEyeClients(guid)/Inspections
   * Get all CTEye inspections for a specific client
   */
  async getInspectionsByClientOData(clientId: string, oDataQuery: string = ""): Promise<ODataResponse<any>> {
    const url = `/OData/CTEyeClients(${clientId})/Inspections?$count=true&${oDataQuery}`;
    const response = await this.client.get<ODataResponse<any>>(url);
    return this.handleResponse<ODataResponse<any>>(response)!;
  }

  /**
   * GET /OData/CTEyeInspectionSummaries?$filter=ctEyeClientId eq {clientId}
   * Get inspection summaries for a specific client
   */
  async getInspectionSummariesByClient(clientId: string, oDataQuery: string = ""): Promise<ODataResponse<CTEyeInspectionSummaryModel>> {
    const clientFilter = `ctEyeClientId eq ${clientId}`;
    const queryWithFilter = oDataQuery.includes('$filter=')
      ? oDataQuery.replace('$filter=', `$filter=${clientFilter} and `)
      : `$filter=${clientFilter}${oDataQuery ? `&${oDataQuery}` : ''}`;
    const url = `/OData/CTEyeInspectionSummaries?$count=true&${queryWithFilter}`;
    const response = await this.client.get<ODataResponse<CTEyeInspectionSummaryModel>>(url);
    return this.handleResponse<ODataResponse<CTEyeInspectionSummaryModel>>(response)!;
  }

  /**
   * GET /OData/UploadSessions/Recent
   * Returns the most recent completed upload session per client
   */
  async getRecentUploadSessionsOData(oDataQuery: string = ""): Promise<ODataResponse<any>> {
    const url = `/OData/UploadSessions/Default.Recent?$count=true&${oDataQuery}`;
    const response = await this.client.get<ODataResponse<any>>(url);
    return this.handleResponse<ODataResponse<any>>(response)!;
  }

  /**
   * GET /OData/UploadSessions
   * Get all upload sessions with OData query support
   */
  async getUploadSessionsOData(oDataQuery: string = ""): Promise<ODataResponse<any>> {
    const url = `/OData/UploadSessions?$count=true&${oDataQuery}`;
    const response = await this.client.get<ODataResponse<any>>(url);
    return this.handleResponse<ODataResponse<any>>(response)!;
  }

  /**
   * GET /OData/CTEyeClients(guid)/UploadSessions
   * Get upload sessions for a specific client
   */
  async getClientUploadSessionsOData(clientId: string, oDataQuery: string = ""): Promise<ODataResponse<any>> {
    const url = `/OData/CTEyeClients(${clientId})/UploadSessions?$count=true&${oDataQuery}`;
    const response = await this.client.get<ODataResponse<any>>(url);
    return this.handleResponse<ODataResponse<any>>(response)!;
  }

  /**
   * GET /OData/CTEyeInspections/Default.MostRecentByClient
   * Get the most recent inspection per client
   */
  async getMostRecentFilesByClientOData(oDataQuery: string = ""): Promise<ODataResponse<any>> {
    const url = `/OData/CTEyeInspections/Default.MostRecentFileByClient?$count=true&${oDataQuery}`;
    const response = await this.client.get<ODataResponse<any>>(url);
    return this.handleResponse<ODataResponse<any>>(response)!;
  }

  /**
   * GET /CTEye/inspections/live
   * Get live inspections
   */
  async getLiveInspections(): Promise<LiveInspectionResponse[] | null> {
    const url = `${this.baseUrl}/inspections/live`;
    const response = await this.client.get<LiveInspectionResponse[]>(url);
    return this.handleResponse<LiveInspectionResponse[]>(response);
  }

  /**
   * GET /CTEye/clients/{clientId}/inspections/live
   * Get live inspections for a specific client
   */
  async getLiveInspectionsByClient(clientId: string): Promise<LiveInspectionResponse[] | null> {
    const url = `${this.baseUrl}/clients/${clientId}/inspections/live`;
    const response = await this.client.get<LiveInspectionResponse[]>(url);
    return this.handleResponse<LiveInspectionResponse[]>(response);
  }
}

export default CTEyeRepo;